// Keeps creation forms in one predictable, keyboard-accessible dialog.
// The existing form handlers remain responsible for validation and saving.
const FLOWS = {
  "create-client": { title: "Add client", action: "Save client", static: true },
  "create-personnel": {
    title: "Add personnel",
    action: "Save personnel",
    static: true,
  },
  "create-site": {
    title: "Add site / location",
    action: "Create location",
    static: true,
  },
  "project-form": {
    title: "Add project",
    action: "Create project",
    static: true,
  },
  "cutoff-form": {
    title: "Create payroll cutoff",
    action: "Prepare payroll",
    static: true,
  },
  userform: { title: "Add user", action: "Create user profile", static: true },
  roleform: { title: "Add role", action: "Create role", static: true },
  invform: { title: "New sales invoice", action: "Issue invoice" },
  jvform: { title: "New journal voucher", action: "Save as draft" },
  depform: { title: "Deploy personnel", action: "Deploy personnel" },
  "work-log-form": {
    title: "Record daily work",
    action: "Submit for approval",
    static: true,
  },
  lvform: {
    title: "File leave request",
    action: "Send for approval",
    static: true,
  },
};

const OPENERS = {
  userform: '[data-act="u-invite"]',
  invform: '[data-act="inv-new"], [data-act="inv-prep"]',
  jvform: '[data-act="jv-new"]',
  depform: '[data-act="dep-new"]',
  "work-log-form": "[data-log-deployment]",
  "jv-line": '[data-act="jv-line"]',
};

let active = null;
let activeView = null;
let fieldValues = null;

function dialogFor(id) {
  return document.querySelector(`[data-create-dialog="${id}"]`);
}

function show(dialog) {
  if (!dialog || dialog.open) return;
  if (typeof dialog.showModal === "function") dialog.showModal();
  else dialog.setAttribute("open", ""); // jsdom and older embedded browsers
}

function close() {
  const dialog = active && dialogFor(active);
  if (dialog?.open && typeof dialog.close === "function") dialog.close();
  else dialog?.removeAttribute("open");
  active = null;
  activeView = null;
  fieldValues = null;
}

function dismiss(dialog) {
  const cancel = dialog?.querySelector(
    '[data-act$="-cancel"], [data-client-edit-cancel]',
  );
  if (cancel) {
    active = null;
    activeView = null;
    fieldValues = null;
    cancel.click();
  } else close();
}

export function finishCreation() {
  close();
}

export function activateCreation(id, view) {
  if (!FLOWS[id]) return;
  active = id;
  activeView = view;
  fieldValues = null;
}

export function rememberCreationFields() {
  if (!active || !FLOWS[active]?.static) return;
  const form = document.getElementById(active);
  if (!form?.closest("dialog")?.open) return;
  fieldValues = [...form.querySelectorAll("input, select, textarea")].map(
    (input) => ({
      value: input.value,
      checked: input.checked,
    }),
  );
}

function restoreCreationFields(form) {
  if (!fieldValues || !FLOWS[form.id]?.static) return;
  [...form.querySelectorAll("input, select, textarea")].forEach(
    (input, index) => {
      const saved = fieldValues[index];
      if (!saved) return;
      if (input.type === "checkbox" || input.type === "radio")
        input.checked = saved.checked;
      else input.value = saved.value;
    },
  );
}

function makeDialog(form, config) {
  const dialog = document.createElement("dialog");
  dialog.className = "creation-dialog";
  dialog.dataset.createDialog = form.id;
  dialog.setAttribute("aria-label", config.title);
  dialog.innerHTML = `<div class="creation-head"><h2></h2><button type="button" class="creation-close ghost" data-create-close aria-label="Close">×</button></div>`;
  dialog.querySelector("h2").textContent = config.title;
  dialog.append(form);
  dialog.addEventListener("cancel", (event) => {
    event.preventDefault();
    dismiss(dialog);
  });
  dialog.addEventListener("click", (event) => {
    if (event.target === dialog) dismiss(dialog);
  });
  return dialog;
}

function createOpener(id, title) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "pri creation-opener";
  button.dataset.createOpen = id;
  button.textContent = title;
  return button;
}

function markOpener(button) {
  button.classList.add("creation-opener");
  if (!button.matches('[data-act="jv-line"]')) button.classList.add("pri");
}

export function enhanceCreationFlows(view) {
  if (active && activeView !== view) close();
  for (const [id, config] of Object.entries(FLOWS)) {
    const form = document.getElementById(id);
    if (!form) continue;
    const details = form.closest("details");
    const wasInline = !details;
    const title =
      details?.querySelector("summary")?.textContent.trim() || config.title;
    const placeholder = document.createComment("creation-flow");
    if (wasInline) form.before(placeholder);
    const dialog = makeDialog(form, { ...config, title });
    if (details) details.replaceWith(createOpener(id, title), dialog);
    else if (id === "roleform" || id === "work-log-form" || id === "lvform") {
      placeholder.replaceWith(createOpener(id, config.title), dialog);
    } else placeholder.replaceWith(dialog);
    if (
      wasInline &&
      !["roleform", "work-log-form", "lvform"].includes(id) &&
      !active
    ) {
      active = id;
      activeView = view;
    }
    if (active === id) {
      restoreCreationFields(form);
      show(dialog);
    }
  }
  for (const selector of Object.values(OPENERS))
    document.querySelectorAll(selector).forEach(markOpener);
  if (active && !dialogFor(active)) close();
}

export function installCreationFlows() {
  document.addEventListener("click", (event) => {
    const button = event.target.closest?.(
      "[data-create-open], [data-create-close]",
    );
    if (!button) return;
    if (button.hasAttribute("data-create-open")) {
      active = button.dataset.createOpen;
      activeView = location.hash.slice(1);
      fieldValues = null;
      const dialog = dialogFor(active);
      show(dialog);
      dialog?.querySelector("form input:not([type=hidden]), form select")?.focus();
    } else if (button.hasAttribute("data-create-close")) {
      dismiss(button.closest("dialog"));
    }
  });
}
